import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

/**
 * PrivacyPolicyPage - Página de Política de Privacidad y Términos de Uso
 * 
 * Página pública (sin autenticación) para cumplir con requisitos de Google Play Store
 * y regulaciones de privacidad (GDPR, Ley 172-13).
 * 
 * Características:
 * - Accesible en /privacy
 * - Diseño limpio y legible con Tailwind Typography
 * - Botón de navegación "Volver" o "Home"
 * - Footer con datos de contacto de Grupo FOM
 */
const PrivacyPolicyPage: React.FC = () => {
  const navigate = useNavigate();

  // Habilitar scroll en body y html cuando se monta la página
  useEffect(() => {
    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalRootOverflow = document.getElementById('root')?.style.overflow || '';

    // Habilitar scroll
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.style.overflow = 'auto';
    }

    // Restaurar estilos originales al desmontar
    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
      if (rootElement) {
        rootElement.style.overflow = originalRootOverflow;
      }
    };
  }, []);

  return (
    <div 
      className="w-full bg-white"
      style={{ 
        height: '100vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch'
      }}
    >
      {/* Header con botón de navegación */}
      <div className="border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="container mx-auto max-w-4xl px-4 py-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors duration-200 text-sm font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Volver al inicio</span>
          </button>
        </div>
      </div>

      {/* Contenido principal */}
      <main className="container mx-auto max-w-4xl px-4 py-8 md:py-12">
        <article className="prose prose-lg prose-gray max-w-none">
          {/* Título principal */}
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            POLÍTICA DE PRIVACIDAD Y TÉRMINOS DE USO DEL ECOSISTEMA THEFOMLABS
          </h1>

          {/* Metadatos */}
          <div className="text-sm text-gray-600 mb-8 space-y-1">
            <p><strong>Última actualización:</strong> 17 de mayo de 2026</p>
            <p><strong>Entidad Responsable:</strong> Grupo FOM, S.R.L.</p>
            <p><strong>RNC:</strong> 133308355</p>
            <p><strong>Domicilio Legal:</strong> B Esquina 1ra No. 1, Santo Domingo, República Dominicana.</p>
            <p><strong>Ámbito:</strong> Ecosistema TheFomLabs (HouseMe, ThePlatform360 (Empresa), Traela, SonMany, La Libreta, WashFlow, GobScore, BankCore, Aroly, Isela, Pawtys, y Elfom Hub (infraestructura centralizada de mensajería))</p>
          </div>

          {/* Sección 1 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              1. INTRODUCCIÓN Y EVOLUCIÓN DEL SERVICIO
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Grupo FOM, S.R.L.</strong> (en adelante, "Nosotros" o "TheFomLabs") opera un ecosistema de aplicaciones interconectadas. Esta política rige el uso de <strong>HouseMe, ThePlatform360 (Empresa), Traela, SonMany, La Libreta, WashFlow, GobScore, BankCore, Aroly, Isela, Pawtys, y Elfom Hub (infraestructura centralizada de mensajería)</strong> y su conexión con el ecosistema.
            </p>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-4">
              <h3 className="font-semibold text-gray-900 mb-2">Cláusula de Evolución y Escalabilidad</h3>
              <p className="text-gray-700 leading-relaxed">
                Grupo FOM se reserva el derecho de expandir las funcionalidades de Houseme para incluir servicios de marketplace, conexión con proveedores técnicos, gestión de domótica y servicios financieros. <strong>El uso continuado de la plataforma tras la implementación de nuevas funciones constituye la aceptación automática de estos nuevos servicios</strong> bajo el marco de privacidad vigente.
              </p>
            </div>
          </section>

          {/* Sección 2 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              2. CUENTA UNIFICADA Y SSO (SINGLE SIGN-ON)
            </h2>
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
              A. Identidad Compartida
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Houseme utiliza un sistema de autenticación centralizado (<strong>TheFomLabs Accounts</strong>). Su perfil básico es compartido a nivel de infraestructura para facilitar el acceso único.
            </p>
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
              B. Consentimiento Específico por App
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Aunque su identidad es compartida, sus datos privados (inventarios, fotos de hogar) están aislados. El acceso a otras aplicaciones del grupo (<em>ThePlatform360</em>, <em>Traela</em>) requiere su <strong>activación voluntaria</strong>. Los datos no se cruzan automáticamente sin su permiso explícito.
            </p>
          </section>

          {/* Sección 2B - Elfom Hub */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              2B. MENSAJERÍA Y WHATSAPP BUSINESS API (ELFOM HUB)
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Elfom Hub (elfom.com) es la infraestructura centralizada de mensajería del ecosistema TheFOM Labs. Opera como canal único de WhatsApp Business API, verificado por Meta, a través del cual las aplicaciones del ecosistema envían comunicaciones a sus usuarios.
            </p>
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
              Tipos de mensajes enviados a través de Elfom Hub
            </h3>
            <ul className="text-gray-700 leading-relaxed mb-4 list-disc list-inside space-y-2">
              <li>Notificaciones transaccionales (confirmaciones, recibos, estados de pedido)</li>
              <li>Alertas de seguridad (cambios de contraseña, accesos nuevos)</li>
              <li>Actualizaciones de servicio (cambios en cotizaciones, estados de solicitud)</li>
              <li>Recordatorios operacionales (citas, vencimientos, confirmaciones)</li>
            </ul>
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
              Consentimiento y opt-in
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              El usuario autoriza expresamente la recepción de mensajes vía WhatsApp al registrarse en cualquier aplicación del ecosistema o al proporcionar su número de teléfono en un formulario con aviso explícito. El consentimiento es por aplicación — activar HouseMe no implica recibir mensajes de La Libreta u otra app sin activación separada.
            </p>
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
              Opt-out
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              El usuario puede cancelar la recepción de mensajes en cualquier momento respondiendo <strong>STOP</strong> al número de Elfom Hub, o desde la configuración de notificaciones dentro de cada aplicación.
            </p>
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
              Retención de mensajes
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Los registros de mensajes enviados se conservan por 90 días para fines de auditoría y soporte, luego son eliminados permanentemente.
            </p>
            <div className="bg-green-50 border-l-4 border-green-500 p-4 my-4">
              <p className="text-gray-700 leading-relaxed">
                Elfom Hub <strong>no utiliza</strong> el canal de WhatsApp para publicidad, promociones no solicitadas ni mensajes de marketing masivo. Todos los mensajes son transaccionales o de servicio.
              </p>
            </div>
          </section>

          {/* Sección 3 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              3. USO DE DATOS Y MONETIZACIÓN
            </h2>
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
              A. Datos Recopilados
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Recopilamos identificación, datos de propiedades, inventarios visuales, datos financieros (hipotecas) y técnicos (logs de errores).
            </p>
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
              B. Personalización Comercial y Publicidad
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Usted autoriza a Houseme a utilizar los metadatos de sus espacios e inventarios para ofrecerle <strong>recomendaciones personalizadas</strong> de productos, servicios y publicidad de terceros. Los datos compartidos con anunciantes para la segmentación serán siempre <strong>agregados o anonimizados</strong>.
            </p>
          </section>

          {/* Sección 4 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              4. INTELIGENCIA ARTIFICIAL Y PROPIEDAD INTELECTUAL
            </h2>
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
              A. Propiedad de los Renders
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Los renders generados son propiedad del usuario. Sin embargo, el <strong>prompt</strong> y los modelos subyacentes son propiedad de Grupo FOM. No garantizamos la registrabilidad de las imágenes generadas por IA.
            </p>
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
              B. Entrenamiento de Modelos Propios
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Usted acepta que Grupo FOM pueda utilizar sus datos <strong>anonimizados y agregados</strong> para el entrenamiento, mejora y desarrollo de sus propios algoritmos de IA.
            </p>
            <div className="bg-red-50 border-l-4 border-red-500 p-4 my-4">
              <h3 className="font-semibold text-gray-900 mb-2">C. INDEMNIDAD TOTAL (ALUCINACIONES)</h3>
              <p className="text-gray-700 leading-relaxed">
                La IA es probabilística. <strong>El usuario asume la total responsabilidad</strong> por decisiones estructurales, de seguridad o diseño basadas en la IA. Grupo FOM <strong>NO es responsable</strong> por daños físicos, materiales o incendios derivados de errores en el análisis de la IA.
              </p>
            </div>
          </section>

          {/* Sección 5 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              5. MARKETPLACE Y OPERACIONES COMERCIALES
            </h2>
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
              A. Intermediación de Servicios
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Houseme podrá facilitar la contratación de terceros. Grupo FOM actúa como <strong>plataforma de conexión</strong>. Al solicitar un servicio, usted autoriza el intercambio de los datos estrictamente necesarios (ubicación, fotos del área).
            </p>
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
              B. Bóveda de Activos Digitales
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              El usuario reconoce que la función de "Bóveda" busca crear un registro histórico de sus activos con sellado de tiempo, pero no garantiza la aceptación legal automática por parte de todas las entidades externas.
            </p>
          </section>

          {/* Sección 6 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              6. SEGURIDAD, TERCEROS Y DPA
            </h2>
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
              A. Sub-procesadores
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Mantenemos <strong>Acuerdos de Procesamiento (DPAs)</strong> con Supabase, Google Cloud, OpenAI y Replicate.
            </p>
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
              B. Transferencia Internacional
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Sus datos pueden ser procesados en servidores en EE.UU., bajo salvaguardas legales (SCCs) compatibles con la Ley 172-13.
            </p>
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
              C. Notificación de Brechas
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Notificaremos brechas de seguridad críticas en un plazo de 72 horas a usuarios y autoridades.
            </p>
          </section>

          {/* Sección 7 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              7. RETENCIÓN Y ASPECTOS FISCALES
            </h2>
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
              A. Eliminación de Cuenta
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Puede borrar su cuenta en la sección "Zona de Peligro" de la app o vía web.
            </p>
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
              B. Excepción Fiscal (DGII)
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              A pesar de la eliminación, conservaremos <strong>registros transaccionales y facturas por hasta 10 años</strong> en cumplimiento con el Código Tributario de la República Dominicana.
            </p>
          </section>

          {/* Sección 8 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              8. POLÍTICA DE CONTENIDO
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              El usuario exonera a Grupo FOM de reclamaciones por derechos de imagen de terceros que aparezcan en sus fotos.
            </p>
          </section>

          {/* Sección 9 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              9. DISPOSICIONES FINALES
            </h2>
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
              A. Transferencia de Activos (M&A)
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              En caso de venta o fusión de Grupo FOM, la base de datos se considera un activo transferible.
            </p>
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
              B. Jurisdicción y Arbitraje
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Cualquier disputa se resolverá mediante <strong>arbitraje privado</strong> y en los tribunales del <strong>Distrito Nacional, República Dominicana</strong>.
            </p>
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
              C. Contacto
            </h3>
            <ul className="text-gray-700 leading-relaxed mb-4 list-disc list-inside space-y-2">
              <li><strong>DPO:</strong> dpo@thefomlabs.com</li>
              <li><strong>Email:</strong> privacy@thefomlabs.com</li>
            </ul>
          </section>

          {/* Enlace a Addendum Legal */}
          <section className="mb-8 mt-12">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                Addendum Legal — Detalle por Aplicaci&oacute;n
              </h2>
              <p className="text-gray-700 mb-4">
                Para consultar la descripci&oacute;n detallada de cada aplicaci&oacute;n del ecosistema, los datos que procesa, su prop&oacute;sito y estado de desarrollo, consulte nuestro addendum legal.
              </p>
              <a
                href="https://thefomlabs.com/legal-addendum"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition-colors duration-200"
              >
                Ver Addendum Legal Completo
                <ArrowLeft className="h-4 w-4 rotate-180" />
              </a>
            </div>
          </section>

          {/* Enlace a Tabla de Transparencia */}
          <section className="mb-8 mt-12">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                Tabla de Transparencia de Datos
              </h2>
              <p className="text-gray-700 mb-4">
                Para consultar el detalle completo de qu&eacute; datos recopilamos, c&oacute;mo los procesamos y d&oacute;nde se almacenan, consulte nuestra tabla de transparencia de datos.
              </p>
              <a
                href="https://thefomlabs.com/transparency"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200"
              >
                Ver Tabla de Transparencia Completa
                <ArrowLeft className="h-4 w-4 rotate-180" />
              </a>
            </div>
          </section>
        </article>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-gray-50 mt-16">
        <div className="container mx-auto max-w-4xl px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Información de contacto */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Grupo FOM, S.R.L.</h3>
              <p className="text-sm text-gray-600 mb-2">
                <strong>Domicilio Legal:</strong><br />
                B Esquina 1ra No. 1<br />
                Santo Domingo, República Dominicana
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <strong>RNC:</strong> 133308355
              </p>
            </div>

            {/* Contacto */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Contacto</h3>
              <p className="text-sm text-gray-600 mb-2">
                <strong>DPO (Data Protection Officer):</strong><br />
                <a href="mailto:dpo@thefomlabs.com" className="text-blue-600 hover:text-blue-800 underline">
                  dpo@thefomlabs.com
                </a>
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <strong>Privacidad:</strong><br />
                <a href="mailto:privacy@thefomlabs.com" className="text-blue-600 hover:text-blue-800 underline">
                  privacy@thefomlabs.com
                </a>
              </p>
              <p className="text-sm text-gray-600">
                <strong>General:</strong><br />
                <a href="mailto:contacto@thefomlabs.com" className="text-blue-600 hover:text-blue-800 underline">
                  contacto@thefomlabs.com
                </a>
              </p>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} Grupo FOM, S.R.L. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
